import * as os from "node:os";
import type { AgentMessage } from "@elyracode/agent-core";
import type { AssistantMessage } from "@elyracode/ai";

/**
 * Builds the body of a GitHub issue for `/bug`.
 *
 * Everything here is deliberately plain data in, string out, so the report can
 * be tested without a TUI and reused by other modes. Redaction runs over the
 * whole body last, so callers do not need to sanitize fields individually.
 */

export const BUG_REPORT_ISSUES_URL = "https://github.com/kwhorne/elyra/issues/new";

/** Longest URL we will hand to a browser/`gh`; beyond this the user gets a file instead. */
export const MAX_ISSUE_URL_LENGTH = 7000;

export interface BugReportInput {
	/** User's one-line description; becomes the title. */
	description: string;
	version: string;
	/** e.g. "node 24.1.0" or "bun 1.2.0" */
	runtime: string;
	model?: { provider: string; id: string };
	thinkingLevel?: string;
	sessionId: string;
	sessionFile?: string;
	messageCounts: { user: number; assistant: number; toolCalls: number };
	extensions: string[];
	extensionErrors: Array<{ path: string; error: string }>;
	skillCount: number;
	/** Most recent assistant error, if the last turn failed. */
	lastError?: string;
	/** Names of the last few tool calls, most recent last. */
	recentTools: string[];
	platform?: { type: string; release: string; arch: string };
	terminal?: string;
}

export interface BugReport {
	title: string;
	body: string;
	/** Prefilled GitHub "new issue" URL, or undefined if the body is too long for a URL. */
	url: string | undefined;
}

const MAX_ERROR_LENGTH = 1500;
const MAX_RECENT_TOOLS = 8;

export function buildBugReport(input: BugReportInput): BugReport {
	const platform = input.platform ?? { type: os.type(), release: os.release(), arch: os.arch() };
	const title = truncate(input.description.trim() || "Bug report", 120);

	const lines: string[] = [];
	lines.push("## Description", "", input.description.trim() || "_(describe what happened and what you expected)_", "");
	lines.push("## Steps to reproduce", "", "1. ", "2. ", "3. ", "");

	lines.push("## Environment", "");
	lines.push(`- Elyra: ${input.version}`);
	lines.push(`- Runtime: ${input.runtime}`);
	lines.push(`- OS: ${platform.type} ${platform.release} (${platform.arch})`);
	if (input.terminal) lines.push(`- Terminal: ${input.terminal}`);
	if (input.model) {
		const thinking = input.thinkingLevel ? ` (thinking: ${input.thinkingLevel})` : "";
		lines.push(`- Model: ${input.model.provider}/${input.model.id}${thinking}`);
	} else {
		lines.push("- Model: none selected");
	}
	lines.push("");

	lines.push("## Session", "");
	lines.push(`- ID: ${input.sessionId}`);
	lines.push(
		`- Messages: ${input.messageCounts.user} user, ${input.messageCounts.assistant} assistant, ${input.messageCounts.toolCalls} tool calls`,
	);
	if (input.recentTools.length > 0) {
		lines.push(`- Recent tools: ${input.recentTools.slice(-MAX_RECENT_TOOLS).join(", ")}`);
	}
	lines.push("");

	if (input.extensions.length > 0 || input.skillCount > 0) {
		lines.push("## Extensions and skills", "");
		if (input.extensions.length > 0) {
			lines.push(`- Extensions: ${input.extensions.join(", ")}`);
		}
		lines.push(`- Skills loaded: ${input.skillCount}`);
		for (const err of input.extensionErrors) {
			lines.push(`- Extension error in \`${basename(err.path)}\`: ${truncate(err.error, 200)}`);
		}
		lines.push("");
	}

	if (input.lastError) {
		lines.push("## Last error", "", "```", truncate(input.lastError, MAX_ERROR_LENGTH), "```", "");
	}

	lines.push(
		"---",
		"_Generated by `/bug`. Review before submitting; secrets are redacted but check paths and content._",
	);

	const body = redact(lines.join("\n"));
	const url = buildIssueUrl(title, body);
	return { title, body, url };
}

/** Pull the fields that need the message history out of the session's messages. */
export function summarizeMessages(messages: readonly AgentMessage[]): {
	counts: BugReportInput["messageCounts"];
	lastError: string | undefined;
	recentTools: string[];
} {
	let user = 0;
	let assistant = 0;
	let toolCalls = 0;
	const recentTools: string[] = [];
	let lastError: string | undefined;

	for (const msg of messages) {
		if (msg.role === "user") {
			user++;
		} else if (msg.role === "assistant") {
			assistant++;
			const am = msg as AssistantMessage;
			for (const block of am.content) {
				if (block.type === "toolCall") {
					toolCalls++;
					recentTools.push(block.name);
				}
			}
			if (am.stopReason === "error") {
				lastError = am.errorMessage ?? "Unknown error";
			} else {
				lastError = undefined;
			}
		}
	}

	return {
		counts: { user, assistant, toolCalls },
		lastError,
		recentTools: recentTools.slice(-MAX_RECENT_TOOLS),
	};
}

function buildIssueUrl(title: string, body: string): string | undefined {
	const params = new URLSearchParams({ title, body, labels: "bug" });
	const url = `${BUG_REPORT_ISSUES_URL}?${params.toString()}`;
	return url.length <= MAX_ISSUE_URL_LENGTH ? url : undefined;
}

// ── Redaction ──

const REDACTION_PATTERNS: Array<[RegExp, string]> = [
	// Known key prefixes
	[/\bsk-or-[A-Za-z0-9_-]{8,}/g, "sk-or-[redacted]"],
	[/\bsk-ant-[A-Za-z0-9_-]{8,}/g, "sk-ant-[redacted]"],
	[/\bsk-[A-Za-z0-9_-]{16,}/g, "sk-[redacted]"],
	[/\bgh[pousr]_[A-Za-z0-9]{20,}/g, "gh*_[redacted]"],
	[/\bAIza[0-9A-Za-z_-]{30,}/g, "AIza[redacted]"],
	[/\bxox[abprs]-[A-Za-z0-9-]{10,}/g, "xox*-[redacted]"],
	[/\bAKIA[0-9A-Z]{16}\b/g, "AKIA[redacted]"],
	// Bearer tokens and generic KEY=value assignments
	[/(Bearer\s+)[A-Za-z0-9._~+/=-]{16,}/gi, "$1[redacted]"],
	[/((?:api[_-]?key|token|secret|password|passwd|authorization)\s*[=:]\s*["']?)[^\s"'&]{6,}/gi, "$1[redacted]"],
	// JWTs
	[/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g, "[redacted-jwt]"],
];

export function redact(text: string): string {
	let out = text;
	for (const [pattern, replacement] of REDACTION_PATTERNS) {
		out = out.replace(pattern, replacement);
	}
	// Home directory paths reveal the username; keep the structure.
	const home = os.homedir();
	if (home && home.length > 1) {
		out = out.split(home).join("~");
	}
	return out;
}

// ── helpers ──

function truncate(text: string, max: number): string {
	return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

function basename(p: string): string {
	const idx = Math.max(p.lastIndexOf("/"), p.lastIndexOf("\\"));
	return idx >= 0 ? p.slice(idx + 1) : p;
}
